import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Route as TourIcon,
  Business as BusinessIcon,
  ContentCopy as ContentIcon,
  Settings as SettingsIcon,
  Assessment as ReportsIcon,
  History as HistoryIcon,
  SmartToy as RobotIcon,
  Sensors as SensorsIcon,
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  LocationOn as WaypointIcon,
  Explore as WayfindingIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectSelectedMuseum, setSelectedMuseum } from '../store/slices/museumSlice';
import { USER_ALLOWED_PATH_SET } from '../config/routeAccess';

const drawerWidth = 240;

interface Museum {
  id: number;
  name: string;
  museum_code: string;
  description: string;
  [key: string]: unknown;
}

interface NavigationProps {
  open: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavigationItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  requiresMuseum: boolean;
  description?: string;
}

const getMuseumScopedDescription = (museumName: string | undefined, suffix: string) =>
  museumName ? `${museumName} ${suffix}` : `All Museums ${suffix}`;

const getNavigationItems = (userRole: string | undefined, selectedMuseumName: string | undefined): NavigationItem[] => {
  const isUserOnly = userRole === 'user';
  const isAdmin = userRole === 'admin';
  const isSupportRole = isAdmin || userRole === 'tech_support';

  const baseMenuItems: NavigationItem[] = [
    {
      text: 'Overview',
      icon: <DashboardIcon />,
      path: '/dashboard',
      requiresMuseum: false,
    },
    {
      text: 'Robots',
      icon: <RobotIcon />,
      path: '/robots',
      requiresMuseum: false,
      description: 'Robot Management & Video Calls',
    },
  ];

  const sharedMenuItems: NavigationItem[] = [
    {
      text: 'Reports',
      icon: <ReportsIcon />,
      path: '/reports',
      requiresMuseum: false,
      description: isSupportRole ? 'Generate & Schedule Reports' : 'View Reports',
    },
    {
      text: 'Concierge Wayfinding',
      icon: <WayfindingIcon />,
      path: '/concierge-wayfinding',
      requiresMuseum: false,
      description: 'Concierge Routing & Wayfinding',
    },
  ];

  const managementMenuItems: NavigationItem[] = [
    {
      text: 'Tours',
      icon: <TourIcon />,
      path: '/tours',
      requiresMuseum: false,
      description: getMuseumScopedDescription(selectedMuseumName, 'Tours'),
    },
    {
      text: 'Content',
      icon: <ContentIcon />,
      path: '/content',
      requiresMuseum: false,
      description: getMuseumScopedDescription(selectedMuseumName, 'Content & Waypoints'),
    },
  ];

  const supportMenuItems: NavigationItem[] = [
    {
      text: 'Waypoints',
      icon: <WaypointIcon />,
      path: '/waypoints',
      requiresMuseum: false,
      description: getMuseumScopedDescription(selectedMuseumName, 'Waypoints'),
    },
    {
      text: 'IoT Dashboard',
      icon: <SensorsIcon />,
      path: '/iot',
      requiresMuseum: false,
      description: 'IoT Device & Door Status Monitoring',
    },
  ];

  const adminMenuItems: NavigationItem[] = [
    {
      text: 'Changelog',
      icon: <HistoryIcon />,
      path: '/changelog',
      requiresMuseum: false,
      description: 'Project release history',
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
      requiresMuseum: false,
      description: 'Email Recipients & User Management',
    },
  ];

  return [
    ...baseMenuItems.filter(item => !isUserOnly || USER_ALLOWED_PATH_SET.has(item.path)),
    ...sharedMenuItems,
    ...(!isUserOnly ? managementMenuItems : []),
    ...(isSupportRole ? supportMenuItems : []),
    ...(isAdmin ? adminMenuItems : []),
  ];
};

const Navigation: React.FC<NavigationProps> = ({ open, onClose, isCollapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAppSelector(state => state.auth);
  const reduxSelectedMuseum = useAppSelector(selectSelectedMuseum);
  const dispatch = useAppDispatch();

  const userRole = user?.role;
  const isSupportRole = userRole === 'admin' || userRole === 'tech_support';
  const assignedMuseumIds = useMemo(
    () => (user?.museum_ids?.length ? user.museum_ids : user?.museum_id ? [user.museum_id] : []),
    [user?.museum_id, user?.museum_ids],
  );
  const isMuseumRestricted = assignedMuseumIds.length > 0 && !isSupportRole;

  const [museums, setMuseums] = useState<Museum[]>([]);

  const fetchMuseums = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/museums/', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const museumsData = data.results || data;
        const filteredMuseums = isMuseumRestricted
          ? museumsData.filter((museum: Museum) => assignedMuseumIds.includes(museum.id))
          : museumsData;
        setMuseums(filteredMuseums);
      }
    } catch (err) {
      console.error('Error fetching museums:', err);
    }
  }, [assignedMuseumIds, isMuseumRestricted]);

  useEffect(() => {
    fetchMuseums();
  }, [fetchMuseums]);

  useEffect(() => {
    if (!isMuseumRestricted) {
      return;
    }

    if (assignedMuseumIds.length === 0) {
      return;
    }

    if (reduxSelectedMuseum && assignedMuseumIds.includes(reduxSelectedMuseum.id)) {
      return;
    }

    const assignedMuseum = museums.find(museum => museum.id === assignedMuseumIds[0]) || null;
    if (assignedMuseum) {
      dispatch(setSelectedMuseum(assignedMuseum));
    }
  }, [assignedMuseumIds, dispatch, isMuseumRestricted, museums, reduxSelectedMuseum]);

  const handleMuseumChange = (museumId: number | string) => {
    if (museumId === 'all') {
      dispatch(setSelectedMuseum(null));
      return;
    }

    const resolvedMuseumId = typeof museumId === 'string' ? Number(museumId) : museumId;
    const museum = museums.find(m => m.id === resolvedMuseumId) || null;
    dispatch(setSelectedMuseum(museum));
  };

  const menuItems = getNavigationItems(userRole, reduxSelectedMuseum?.name);

  const handleNavigation = (path: string, requiresMuseum: boolean) => {
    if (requiresMuseum && !reduxSelectedMuseum) {
      navigate('/museums');
      if (isMobile) onClose();
      return;
    }
    navigate(path);
    if (isMobile) onClose();
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {!isCollapsed && (
          <Typography variant="h6" color="primary" fontWeight="bold">
            Robot Dashboard
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          sx={{ ml: isCollapsed ? 'auto' : 0 }}
        >
          {isCollapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Divider />
      {!isCollapsed && (
        <Box sx={{ p: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Museum</InputLabel>
            <Select
              value={reduxSelectedMuseum?.id || (isMuseumRestricted ? assignedMuseumIds[0] || '' : 'all')}
              onChange={e => handleMuseumChange(e.target.value)}
              label="Museum"
              disabled={museums.length === 0}
            >
              {!isMuseumRestricted && (
                <MenuItem value={'all'}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2">All Museums</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Show all data
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              )}
              {museums.map(museum => (
                <MenuItem key={museum.id} value={museum.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2">{museum.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {museum.museum_code}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {reduxSelectedMuseum && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {reduxSelectedMuseum.description}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Divider />
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {menuItems.map(item => {
          const isActive = location.pathname === item.path;
          const isDisabled = item.requiresMuseum && !reduxSelectedMuseum;
          return (
            <ListItem key={item.text} disablePadding sx={{ justifyContent: 'center' }}>
              <Tooltip
                title={isDisabled ? 'Please select a museum first' : item.description || item.text}
                placement="right"
              >
                <ListItemButton
                  onClick={() => handleNavigation(item.path, item.requiresMuseum)}
                  selected={isActive}
                  disabled={isDisabled}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    opacity: isDisabled ? 0.6 : 1,
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                      '& .MuiListItemIcon-root': { color: theme.palette.primary.contrastText },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                      minWidth: isCollapsed ? 'auto' : 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={item.text}
                      secondary={item.description && !isActive ? item.description : undefined}
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={2} justifyContent={isCollapsed ? 'center' : 'flex-start'}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
            {user?.username?.charAt(0).toUpperCase()}
          </Avatar>
          {!isCollapsed && (
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {user?.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isCollapsed ? 72 : drawerWidth,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isCollapsed ? 72 : drawerWidth,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Navigation;
